import React, { useState, useMemo, useRef } from 'react';
import {
  Paper, Box, Button, Typography, TextField, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
  Chip, Snackbar, Accordion, AccordionSummary, AccordionDetails,
  Popper, MenuList, MenuItem, ClickAwayListener
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getBulkSmsUserPreview, sendBulkSms } from '../api';
import { useTheme } from '@mui/material/styles';
import PageHeader from './common/PageHeader';
import { Link } from 'react-router-dom';

export default function BulkSms() {
  const theme = useTheme();
  const [userIdsInput, setUserIdsInput] = useState('');
  const [message, setMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [notFoundIds, setNotFoundIds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [hasPreview, setHasPreview] = useState(false);
  
  // CSV upload state
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Autocomplete state
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const messageInputRef = useRef(null);
  const cursorPositionRef = useRef(0);

  // Supported variables
  const SUPPORTED_VARIABLES = ['username', 'tokenBalance', 'redeemableTokenBalance', 'referralUrl'];

  // Helper: Replace variables in message with user data
  const replaceVariables = (msg, user) => {
    if (!msg || !user) return msg;
    
    let result = msg;
    result = result.replace(/\{\{username\}\}/g, user.username || '');
    result = result.replace(/\{\{tokenBalance\}\}/g, user.balance != null ? String(user.balance) : '');
    result = result.replace(/\{\{redeemableTokenBalance\}\}/g, user.redeemable != null ? String(user.redeemable) : '');
    
    const referralUrl = user.username ? `beta.bidsy.com/${user.username}` : '';
    result = result.replace(/\{\{referralUrl\}\}/g, referralUrl);
    
    return result;
  };

  // Helper: Extract variables from message
  const extractVariables = (msg) => {
    const regex = /\{\{([^}]+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(msg)) !== null) {
      matches.push(match[1]);
    }
    return [...new Set(matches)]; // Remove duplicates
  };

  // Check for invalid variables
  const invalidVariables = useMemo(() => {
    const found = extractVariables(message);
    return found.filter(v => !SUPPORTED_VARIABLES.includes(v));
  }, [message]);

  // Check if message has variables
  const hasVariables = useMemo(() => {
    return /\{\{[^}]+\}\}/.test(message);
  }, [message]);

  // Autocomplete detection and filtering
  const detectVariableTyping = (text, cursorPos) => {
    // Get text up to cursor
    const textBeforeCursor = text.substring(0, cursorPos);
    
    // Look for incomplete variable pattern: {{ followed by optional letters
    const match = textBeforeCursor.match(/\{\{([a-zA-Z]*)$/);
    
    if (match) {
      const partialVariable = match[1].toLowerCase();
      const matchingVars = SUPPORTED_VARIABLES.filter(v => 
        v.toLowerCase().startsWith(partialVariable)
      );
      
      return {
        isTyping: true,
        partial: match[1],
        startPos: cursorPos - match[0].length,
        matches: matchingVars
      };
    }
    
    return { isTyping: false, matches: [] };
  };

  // Handle message input changes
  const handleMessageChange = (e) => {
    const newMessage = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    setMessage(newMessage);
    cursorPositionRef.current = cursorPos;
    
    // Check for variable typing
    const detection = detectVariableTyping(newMessage, cursorPos);
    
    if (detection.isTyping && detection.matches.length > 0) {
      setAutocompleteOptions(detection.matches);
      setAutocompleteOpen(true);
      setSelectedIndex(0);
    } else {
      setAutocompleteOpen(false);
      setAutocompleteOptions([]);
    }
  };

  // Handle cursor position changes (clicking, arrow keys, etc.)
  const handleMessageSelect = (e) => {
    const cursorPos = e.target.selectionStart;
    cursorPositionRef.current = cursorPos;
    
    // Re-check if we're still in a variable context
    const detection = detectVariableTyping(message, cursorPos);
    
    if (detection.isTyping && detection.matches.length > 0) {
      setAutocompleteOptions(detection.matches);
      setAutocompleteOpen(true);
      setSelectedIndex(0);
    } else {
      setAutocompleteOpen(false);
      setAutocompleteOptions([]);
    }
  };

  // Insert selected variable
  const insertVariable = (variableName) => {
    const cursorPos = cursorPositionRef.current;
    const textBeforeCursor = message.substring(0, cursorPos);
    const textAfterCursor = message.substring(cursorPos);
    
    // Find where the {{ started
    const match = textBeforeCursor.match(/\{\{([a-zA-Z]*)$/);
    if (!match) return;
    
    const startPos = cursorPos - match[0].length;
    const newText = 
      message.substring(0, startPos) + 
      `{{${variableName}}}` + 
      textAfterCursor;
    
    setMessage(newText);
    setAutocompleteOpen(false);
    setAutocompleteOptions([]);
    
    // Set cursor position after the inserted variable
    const newCursorPos = startPos + `{{${variableName}}}`.length;
    setTimeout(() => {
      if (messageInputRef.current) {
        const input = messageInputRef.current.querySelector('textarea');
        if (input) {
          input.focus();
          input.setSelectionRange(newCursorPos, newCursorPos);
          cursorPositionRef.current = newCursorPos;
        }
      }
    }, 0);
  };

  // Handle keyboard navigation in autocomplete
  const handleKeyDown = (e) => {
    if (!autocompleteOpen) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < autocompleteOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (autocompleteOptions[selectedIndex]) {
        insertVariable(autocompleteOptions[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setAutocompleteOpen(false);
      setAutocompleteOptions([]);
    }
  };

  const parseUserIds = (input) => {
    return input
      .split(/[\r\n,]+/)
      .map(id => id.trim())
      .filter(id => id !== '')
      .map(id => Number(id))
      .filter(id => Number.isFinite(id) && id > 0);
  };

  // Parse CSV content and extract user IDs
  const parseCsvUserIds = (csvContent) => {
    const lines = csvContent
      .split(/[\r\n]+/)
      .map(line => line.trim())
      .filter(line => line !== '');
    
    if (lines.length === 0) {
      return [];
    }
    
    // Check if first line is a header (non-numeric)
    const firstLine = lines[0];
    const firstValue = firstLine.split(',')[0].trim();
    const hasHeader = isNaN(Number(firstValue)) || firstValue === '';
    
    // Skip header if present
    const dataLines = hasHeader ? lines.slice(1) : lines;
    
    // Extract IDs (assume single column or first column)
    const ids = dataLines
      .map(line => {
        const value = line.split(',')[0].trim();
        return Number(value);
      })
      .filter(id => Number.isFinite(id) && id > 0);
    
    return ids;
  };

  // Merge existing and new IDs, removing duplicates
  const mergeUserIds = (existingInput, newIds) => {
    const existingIds = parseUserIds(existingInput);
    const combined = [...new Set([...existingIds, ...newIds])];
    return combined.join(', ');
  };

  // Handle CSV file upload
  const handleCsvUpload = (event) => {
    const file = event.target.files?.[0];
    
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadError('Please upload a CSV file');
      setToast({ severity: 'error', msg: 'Invalid file type. Please upload a CSV file.' });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File is too large (max 5MB)');
      setToast({ severity: 'error', msg: 'File is too large. Maximum size is 5MB.' });
      return;
    }

    setUploading(true);
    setUploadError(null);

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvContent = e.target?.result;
        
        if (typeof csvContent !== 'string') {
          throw new Error('Failed to read file content');
        }

        const newIds = parseCsvUserIds(csvContent);
        
        if (newIds.length === 0) {
          setUploadError('No valid user IDs found in CSV');
          setToast({ severity: 'warning', msg: 'No valid user IDs found in the CSV file.' });
          setUploading(false);
          return;
        }

        // Merge with existing IDs
        const mergedInput = mergeUserIds(userIdsInput, newIds);
        const totalIds = parseUserIds(mergedInput);

        if (totalIds.length > 1000) {
          setUploadError('Too many user IDs (max 1000)');
          setToast({ 
            severity: 'error', 
            msg: `Total user IDs would exceed limit (${totalIds.length}/1000). Please reduce the number of IDs.` 
          });
          setUploading(false);
          return;
        }

        setUserIdsInput(mergedInput);
        setUploadError(null);
        setToast({ 
          severity: 'success', 
          msg: `Successfully added ${newIds.length} user ID(s) from CSV. Total: ${totalIds.length}` 
        });
        
        // Reset file input
        event.target.value = '';
      } catch (error) {
        console.error('CSV parse error:', error);
        setUploadError('Failed to parse CSV file');
        setToast({ 
          severity: 'error', 
          msg: 'Failed to parse CSV file. Please ensure it is properly formatted.' 
        });
      } finally {
        setUploading(false);
      }
    };

    reader.onerror = () => {
      setUploadError('Failed to read file');
      setToast({ severity: 'error', msg: 'Failed to read file. Please try again.' });
      setUploading(false);
    };

    reader.readAsText(file);
  };

  const handlePreview = async () => {
    const userIds = parseUserIds(userIdsInput);
    
    if (userIds.length === 0) {
      setToast({ severity: 'error', msg: 'Please enter at least one valid user ID' });
      return;
    }

    if (userIds.length > 1000) {
      setToast({ severity: 'error', msg: 'Too many user IDs (max 1000)' });
      return;
    }

    try {
      setLoading(true);
      setHasPreview(false);
      const result = await getBulkSmsUserPreview(userIds);
      
      if (result.success) {
        setUsers(result.users || []);
        setNotFoundIds(result.notFoundIds || []);
        setStats(result.stats || null);
        setHasPreview(true);
        
        if (result.users.length === 0) {
          setToast({ severity: 'warning', msg: 'No valid users found' });
        } else if (result.stats.optedOut > 0) {
          setToast({ 
            severity: 'warning', 
            msg: `${result.stats.optedOut} user(s) have opted out and cannot receive SMS` 
          });
        }
      } else {
        setToast({ severity: 'error', msg: result.message || 'Failed to load user preview' });
      }
    } catch (e) {
      setToast({ severity: 'error', msg: e?.response?.data?.message || 'Failed to load user preview' });
      setHasPreview(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      setToast({ severity: 'error', msg: 'Please enter a message' });
      return;
    }

    if (message.length > 1000) {
      setToast({ severity: 'error', msg: 'Message is too long (max 1000 chars)' });
      return;
    }

    if (stats && stats.optedOut > 0) {
      setToast({ severity: 'error', msg: 'Cannot send: some users have opted out' });
      return;
    }

    const eligibleUserIds = users
      .filter(u => !u.isOptedOut)
      .map(u => u.id);

    if (eligibleUserIds.length === 0) {
      setToast({ severity: 'error', msg: 'No eligible users to send SMS' });
      return;
    }

    try {
      setSending(true);
      const result = await sendBulkSms(eligibleUserIds, message.trim());
      
      if (result.success) {
        setToast({ 
          severity: 'success', 
          msg: result.message || `SMS queued for ${result.sentCount} user(s)` 
        });
        
        // Reset form
        setUserIdsInput('');
        setMessage('');
        setUsers([]);
        setNotFoundIds([]);
        setStats(null);
        setHasPreview(false);
      } else {
        setToast({ severity: 'error', msg: result.message || 'Failed to send SMS' });
      }
    } catch (e) {
      setToast({ severity: 'error', msg: e?.response?.data?.message || 'Failed to send SMS' });
    } finally {
      setSending(false);
    }
  };

  const canSend = hasPreview && stats && stats.canSend > 0 && message.trim() && !sending;

  return (
    <>
      <PageHeader 
        title="Bulk SMS" 
        subtitle="Send SMS messages to multiple users at once"
      />
      
      <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>
                Personalize Messages with Variables
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Use these variables in your message to personalize it for each user. Variables will be replaced with actual user data.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <Box component="code" sx={{ backgroundColor: '#e0e0e0', px: 1, py: 0.5, borderRadius: 1, fontFamily: 'monospace' }}>
                  {'{{username}}'}
                </Box>
                {' - User\'s username'}
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <Box component="code" sx={{ backgroundColor: '#e0e0e0', px: 1, py: 0.5, borderRadius: 1, fontFamily: 'monospace' }}>
                  {'{{tokenBalance}}'}
                </Box>
                {' - Total token balance'}
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <Box component="code" sx={{ backgroundColor: '#e0e0e0', px: 1, py: 0.5, borderRadius: 1, fontFamily: 'monospace' }}>
                  {'{{redeemableTokenBalance}}'}
                </Box>
                {' - Redeemable token balance'}
              </Typography>
              <Typography component="li" variant="body2">
                <Box component="code" sx={{ backgroundColor: '#e0e0e0', px: 1, py: 0.5, borderRadius: 1, fontFamily: 'monospace' }}>
                  {'{{referralUrl}}'}
                </Box>
                {' - Referral URL (beta.bidsy.com/username)'}
              </Typography>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Example:</strong> "Hi {'{{username}}'}, you have {'{{tokenBalance}}'} tokens! Share your link: {'{{referralUrl}}'}"
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {invalidVariables.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> Unrecognized variables detected: {invalidVariables.map(v => `{{${v}}}`).join(', ')}
            <br />
            These will be left as-is in the message. Please check the supported variables list above.
          </Typography>
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Enter User IDs</Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="User IDs (comma-separated)"
          placeholder="e.g., 123, 456, 789 or one per line"
          value={userIdsInput}
          onChange={(e) => setUserIdsInput(e.target.value)}
          helperText="Enter user IDs separated by commas or newlines. Maximum 1000 users."
          sx={{ mb: 1 }}
        />
        
        {/* CSV Upload Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            disabled={uploading}
            size="small"
          >
            {uploading ? 'Uploading...' : 'Upload CSV'}
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={handleCsvUpload}
            />
          </Button>
          {uploadError && (
            <Typography variant="caption" color="error" sx={{ ml: 2 }}>
              {uploadError}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ position: 'relative', mb: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message"
            placeholder="Enter your SMS message here... (Type {{ to see available variables)"
            value={message}
            onChange={handleMessageChange}
            onSelect={handleMessageSelect}
            onKeyDown={handleKeyDown}
            helperText={`${message.length}/1000 characters`}
            inputProps={{ maxLength: 1000 }}
            ref={messageInputRef}
          />
          
          {/* Variable Autocomplete Dropdown */}
          <Popper
            open={autocompleteOpen}
            anchorEl={messageInputRef.current}
            placement="bottom-start"
            style={{ zIndex: 1300 }}
          >
            <ClickAwayListener onClickAway={() => setAutocompleteOpen(false)}>
              <Paper
                elevation={8}
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  minWidth: 200,
                  mt: 0.5
                }}
              >
                <MenuList dense>
                  {autocompleteOptions.map((variable, index) => (
                    <MenuItem
                      key={variable}
                      selected={index === selectedIndex}
                      onClick={() => insertVariable(variable)}
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                          }
                        }
                      }}
                    >
                      {`{{${variable}}}`}
                    </MenuItem>
                  ))}
                </MenuList>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handlePreview}
            disabled={loading || !userIdsInput.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Preview Users'}
          </Button>
          
          {hasPreview && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSend}
              disabled={!canSend}
            >
              {sending ? <CircularProgress size={24} /> : `Send SMS to ${stats?.canSend || 0} User(s)`}
            </Button>
          )}
        </Box>
      </Paper>

      {hasPreview && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>User Preview</Typography>
          
          {stats && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Total: ${stats.total}`} color="primary" />
              <Chip 
                label={`Can Send: ${stats.canSend}`} 
                color="success" 
                variant={stats.canSend > 0 ? 'filled' : 'outlined'}
              />
              {stats.optedOut > 0 && (
                <Chip label={`Opted Out: ${stats.optedOut}`} color="error" />
              )}
              {notFoundIds.length > 0 && (
                <Chip label={`Not Found: ${notFoundIds.length}`} color="warning" />
              )}
            </Box>
          )}

          {notFoundIds.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              User IDs not found: {notFoundIds.join(', ')}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No users found
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>User ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  {hasVariables && message.trim() && (
                    <TableCell sx={{ fontWeight: 'bold' }}>Message Preview</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const personalizedMsg = hasVariables && message.trim() 
                    ? replaceVariables(message, user)
                    : '';
                  const previewText = personalizedMsg.length > 80 
                    ? personalizedMsg.substring(0, 80) + '...' 
                    : personalizedMsg;

                  return (
                    <TableRow 
                      key={user.id}
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                        ...(user.isOptedOut ? { opacity: 0.6, backgroundColor: '#ffebee' } : {})
                      }}
                    >
                      <TableCell>
                        <Link
                          to={`/user/${user.id}`}
                          state={{ from: { label: 'Bulk SMS', path: '/bulk-sms' } }}
                          style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 600 }}
                        >
                          {user.id}
                        </Link>
                      </TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.phone}</TableCell>
                      <TableCell>
                        {user.isOptedOut ? (
                          <Chip size="small" label="Opted Out (STOP)" color="error" />
                        ) : (
                          <Chip size="small" label="Active" color="success" variant="outlined" />
                        )}
                      </TableCell>
                      {hasVariables && message.trim() && (
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontStyle: 'italic',
                              color: 'text.secondary',
                              fontSize: '0.875rem'
                            }}
                          >
                            {previewText}
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : null}
      </Snackbar>
    </>
  );
}

